import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Loader, Upload, X } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { ImageToImageApis } from '../../services/imageApis';
import { saveGeneratedImage, setFileName, setOriginalImage } from 'store/slices/imageToImage';
import toast from 'react-hot-toast';
import { STYLE_OPTIONS } from '../../constants/data';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { GoogleReCaptcha } from 'react-google-recaptcha-v3';
import { fileToUrl } from 'api/commonApi';

interface StyleOption {
  id: string;
  label: string;
  imageUrl: string;
}

const SelectFilePanel: React.FC = () => {
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string>('ghibli');
  const [isDragging, setIsDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [refreshReCaptcha, setRefreshReCaptcha] = useState(false);

  const MAX_FILE_SIZE_MB = 5;

  // Helper function to convert File to base64
  const fileToBase64 = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result as string);
      reader.onerror = error => reject(error);
    });
  };

  const validateFile = (file: File) => {
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`File size must be less than ${MAX_FILE_SIZE_MB}MB`);
      return null;
    }

    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (PNG, JPEG)');
      return null;
    }

    setError(null);
    setSelectedImage(file);
    return file;
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      validateFile(file);
    }
    e.target.value = '';
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      validateFile(file);
    }
  };

  const clearSelectedImage = () => {
    setSelectedImage(null);
  };

  const handleReCaptchaVerify = useCallback(async () => {
    if (!executeRecaptcha) return;
    return await executeRecaptcha('ImageToImageRecaptcha');
  }, [executeRecaptcha]);

  const { mutate: mutateGhibli, isPending: isGhibliPending } = useMutation({
    mutationKey: ['generate-ghibli-image'],
    mutationFn: async ({ image }: { image: File }) => {
      const formData = new FormData();
      formData.append('image', image);
      return ImageToImageApis.generateGhibliImage(formData);
    },
    onSuccess: (data) => {
      console.log("data", data);
      dispatch(saveGeneratedImage({
        generatedImageUrl: data.data,
        selectedStyle: 'ghibli'
      }));
      setIsProcessing(false);
      navigate('/image-to-image/result');
    },
    onError: (error) => {
      console.error('Error:', error);
      toast.error('Failed to generate image. Please try again.');
      setIsProcessing(false);
    }
  });

  const { mutate: mutateStyle, isPending: isStylePending } = useMutation({
    mutationKey: ['generate-styled-image'],
    mutationFn: async ({ image_url, feature_name }: { image_url: string; feature_name: string }) => {
      return ImageToImageApis.imageToImage({
        image_url,
        feature_name
      });
    },
    onSuccess: (data) => {
      console.log("data", data);
      dispatch(saveGeneratedImage({
        generatedImageUrl: data.data,
        selectedStyle
      }));
      setIsProcessing(false);
      navigate('/image-to-image/result');
    },
    onError: (error) => {
      console.error('Error:', error);
      toast.error('Failed to generate image. Please try again.');
      setIsProcessing(false);
    }
  });

  const handleGenerate = async () => {
    if (!selectedImage) return;
    
    setIsProcessing(true);

    try {
      const base64 = await fileToBase64(selectedImage);
      
      dispatch(setOriginalImage({ file: selectedImage, base64 }));
      dispatch(setFileName({ fileName: selectedImage.name }));

      if (selectedStyle === 'ghibli') {
        mutateGhibli({ image: selectedImage });
      } else {
        const fileUrlResponse = await fileToUrl(selectedImage);
        const imageUrl = fileUrlResponse.data.url;
        mutateStyle({
          image_url: imageUrl,
          feature_name: selectedStyle
        });
      }
    } catch (error) {
      console.error('Error processing image:', error);
      toast.error('Failed to process image. Please try again.');
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    handleReCaptchaVerify();
  }, [handleReCaptchaVerify]);

  const isLoading = isProcessing || isGhibliPending || isStylePending;

  const onVerify = useCallback((token: string) => {
    console.log('reCAPTCHA verified');
  }, []);

  return (
    <div className="w-full max-w-2xl mx-auto space-y-8">
      {/* Image Upload Section */}
      <div>
        <div
          className={`relative flex flex-col items-center justify-center w-full h-[172px] border-2 rounded-lg border-dashed transition-colors ${
            isDragging
              ? 'border-primary bg-primary/5'
              : 'border-primary hover:border-primary/50 cursor-pointer'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
        >
          {selectedImage ? (
            <div className="relative w-full h-full">
              <img
                src={URL.createObjectURL(selectedImage)}
                alt="Preview"
                className="w-full h-full object-contain"
              />
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  clearSelectedImage();
                }}
                className="absolute top-2 right-2 p-1 bg-black/50 rounded-full hover:bg-black/70"
              >
                <X className="w-4 h-4 text-white" />
              </button>
            </div>
          ) : (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/png,image/jpeg"
                className="hidden"
                onChange={handleFileChange}
              />
              <Upload className="mx-auto h-7 w-7 text-primary" />
              <p className="mt-4 font-medium text-text-primary">
                Drag and drop or click to choose image
              </p>
              <p className="mt-1 text-xs text-gray-500">PNG, JPEG (UP TO 5MB)</p>
            </>
          )}
        </div>
        {error && <p className="text-red-500 text-sm mt-2">{error}</p>}
      </div>

      {/* Style Selection Section */}
      <div>
        <h3 className="text-base font-medium mb-4 text-text-primary">
          Select style
        </h3>
        <div className="grid grid-cols-5 gap-4">
          {STYLE_OPTIONS.map((style) => (
            <div key={style.id} className="flex flex-col items-center gap-2">
              <button
                onClick={() => setSelectedStyle(style.id)}
                className={`relative aspect-square w-full rounded-lg overflow-hidden border-2 transition-all ${
                  selectedStyle === style.id
                    ? 'border-primary ring-2 ring-primary/20'
                    : 'border-transparent hover:border-primary/50'
                }`}
              >
                <img
                  src={style.imageUrl}
                  alt={style.label}
                  className="w-full h-full object-cover"
                />
              </button>
              <span className="text-xs font-medium text-text-primary">
                {style.label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Generate Button */}
      <button
        onClick={handleGenerate}
        disabled={!selectedImage || isLoading}
        className={`w-full py-3 px-4 rounded-lg flex items-center justify-center gap-2 ${
          !selectedImage || isLoading
            ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
            : 'bg-primary text-white hover:bg-primary/90'
        }`}
      >
        {isLoading ? (
          <>
            <Loader className="h-5 w-5 animate-spin" />
            <span>Generating...</span>
          </>
        ) : (
          <span className="text-lg font-medium">Generate image</span>
        )}
      </button>

      {/* Add GoogleReCaptcha component */}
      {!refreshReCaptcha && (
        <GoogleReCaptcha 
          key={refreshReCaptcha ? 'refresh' : 'initial'} 
          action="ImageToImageRecaptcha" 
          onVerify={onVerify} 
        />
      )}
    </div>
  );
};

export default SelectFilePanel;
